import { createResourceMiddleware, createResourceTemplate } from '@dojo/framework/core/middleware/resources';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import Chip from '@dojo/widgets/chip';
import { isEmpty } from 'lodash-es';
import { publish } from 'pub-sub';
import { Keys } from '@holmescorp/common';
import { createNlsMiddleware, keymap, pathmap, quizmap, userstate } from '@holmescorp/lcc-state';
import { numeral } from '@holmescorp/numeral';
import Tree from '@holmescorp/base-widgets/tree';
import { css } from '../css/toc.m.css';
const resource = createResourceMiddleware();
const factory = create({
    nls: createNlsMiddleware('ditabook'),
    theme,
    userstate,
    resource
}).properties();
export const TableOfContents = factory(({ id, properties, middleware: { nls, theme, userstate, resource } }) => {
    const { isPlaceholder, messages: { quiz: quizText = '[ditabook] quiz', flashcards: flashcardText = '[ditabook] flashcards', topic_root_order = '[]' } } = nls();
    if (isPlaceholder) {
        return null;
    }
    const { onClose } = properties();
    const themeCss = theme.classes(css);
    const overrides = {
        '@dojo/widgets/tree': {
            root: [themeCss.treeRoot],
            node: [themeCss.treeNode],
            nodeParent: [themeCss.indented],
            contentWrapper: [themeCss.contentWrapper],
            content: [themeCss.content],
            leaf: [themeCss.leaf],
            title: [themeCss.title],
            expander: [themeCss.expander]
        }
    };
    const lang = window.hcstatic.portal_language;
    const collator = new Intl.Collator(lang, { numeric: true, sensitivity: 'base' });
    const topicRootOrder = JSON.parse(topic_root_order);
    const getRoots = (status) => {
        return Object.keys(status)
            .filter((key) => status[key].parent === null)
            .sort((a, b) => {
            if (topicRootOrder.length > 0) {
                return topicRootOrder.indexOf(a) - topicRootOrder.indexOf(b);
            }
            else {
                return collator.compare(a, b);
            }
        })
            .map((id) => {
            const { title: value } = keymap.get(id);
            const { data } = status[id];
            return { id, value, parent: 'root', hasChildren: true, data, nodeType: 'root' };
        });
    };
    const getTopicsets = (parent, status) => {
        return Object.keys(status)
            .filter((key) => status[key].parent === parent)
            .sort(collator.compare)
            .map((id) => {
            const { title: value } = keymap.get(id);
            const { quiz: quizId, flashcards: fcId } = quizmap.get(id) || {};
            const { data } = status[id];
            const quiz = quizId
                ? {
                    id: quizId,
                    type: 'quiz',
                    value: quizText,
                    score: `${numeral(data.percent).format('0%')}` || ''
                }
                : undefined;
            const flashcards = fcId
                ? {
                    id: fcId,
                    type: 'flashcards',
                    value: flashcardText
                }
                : undefined;
            return { id, value, parent, hasChildren: true, data, quiz, flashcards, nodeType: 'parent' };
        });
    };
    const getTopicgroups = (parent, status) => {
        return Object.keys(status)
            .filter((key) => keymap.has(key) && status[key].parent === parent)
            .sort(collator.compare)
            .map((id) => {
            const { title: value, path } = keymap.get(id);
            const { data } = status[id];
            const completed = data.complete === data.total;
            return { id, value, parent, hasChildren: false, path, completed, data, nodeType: 'leaf' };
        });
    };
    const getLinks = (parents) => {
        return parents.map((parent) => {
            const { id, quiz, flashcards } = parent;
            return {
                id: `${id}-links`,
                value: '',
                parent: id,
                hasChildren: false,
                quiz,
                flashcards,
                nodeType: 'link'
            };
        });
    };
    const getNodes = () => {
        const { get: uget, path: upath } = userstate;
        const status = uget(upath('status'));
        const nodes = getRoots(status);
        const topicsets = nodes
            .map((root) => {
            const { id } = root;
            return getTopicsets(id, status);
        })
            .flat();
        nodes.push(...topicsets);
        const topicgroups = topicsets
            .map((topicset) => {
            const { id } = topicset;
            return getTopicgroups(id, status);
        })
            .flat();
        nodes.push(...topicgroups);
        nodes.push(...getLinks(topicsets));
        return nodes;
    };
    const itemRenderer = (node) => {
        const renderNode = (node) => {
            const { id, value, path, data, completed, quiz, flashcards, nodeType } = node;
            const time = data?.readtime;
            const links = [];
            if (!isEmpty(quiz)) {
                links.push(quiz);
            }
            if (!isEmpty(flashcards)) {
                links.push(flashcards);
            }
            switch (nodeType) {
                case 'root':
                    return v('h1', {}, [value]);
                case 'leaf':
                    return v('div', { classes: [themeCss.item, themeCss.leafItem] }, [
                        v('a', {
                            role: 'link',
                            tabIndex: 0,
                            onclick: (event) => {
                                event.stopPropagation();
                                const { view } = pathmap.get(path);
                                view();
                            },
                            onkeydown: (event) => {
                                const { view } = pathmap.get(path);
                                switch (event.key) {
                                    case Keys.Enter:
                                    case Keys.Space:
                                    case Keys.Spacebar:
                                        event.preventDefault();
                                        view();
                                }
                            }
                        }, [v('h3', {}, [value])]),
                        completed
                            ? v('div', { classes: [themeCss.complete] }, [
                                w('TopicStatusIcon', { key: id, useQuizScore: false })
                            ])
                            : null,
                        !completed && time ? v('span', { classes: [themeCss.readtime] }, [time]) : null
                    ]);
                case 'parent':
                    return v('h2', {}, [value]);
                case 'link':
                    return links.map((link) => {
                        const { value, type, score = '' } = link;
                        const { parent } = node;
                        return v('a', {
                            role: 'link',
                            tabIndex: 0,
                            onclick: (event) => {
                                event && event.stopPropagation();
                                const { path } = keymap.get(parent);
                                const { view } = pathmap.get(`${path}/${type}`);
                                view();
                                onClose && onClose();
                            },
                            onkeydown: (event) => {
                                const { path } = keymap.get(parent);
                                const { view } = pathmap.get(`${path}/${type}`);
                                switch (event.key) {
                                    case Keys.Enter:
                                    case Keys.Space:
                                    case Keys.Spacebar:
                                        event.preventDefault();
                                        view();
                                        onClose && onClose();
                                }
                            }
                        }, [
                            value,
                            score && parseInt(score) > 0
                                ? v('div', { classes: [themeCss.chip] }, [
                                    w(Chip, {
                                        classes: { '@dojo/widgets/chip': { root: [themeCss.chipRoot] } }
                                    }, [{ label: score }])
                                ])
                                : null
                        ]);
                    });
                default:
                    return null;
            }
        };
        return renderNode(node);
    };
    const nodes = getNodes();
    const template = createResourceTemplate('id');
    const overview = v('div', { classes: [themeCss.video] }, [
        v('a', {
            role: 'link',
            tabIndex: 0,
            onclick: (event) => {
                event && event.stopPropagation();
                publish('site/video/13');
                onClose && onClose();
            },
            onkeydown: (event) => {
                switch (event.key) {
                    case Keys.Enter:
                    case Keys.Space:
                    case Keys.Spacebar:
                        event.preventDefault();
                        publish('site/video/13');
                        onClose && onClose();
                }
            }
        }, [v('h1', {}, ['Learning System Overview video'])])
    ]);
    return v('div', {
        id: `toc-tree-${id}`,
        xqa: 'table-of-contents',
        classes: [themeCss.root, theme.variant()]
    }, [
        overview,
        w(Tree, {
            classes: overrides,
            key: 'toc',
            resource: resource({ template: template({ id, data: nodes }) })
        }, [itemRenderer])
    ]);
});
export default TableOfContents;
